import React, { useMemo, useState } from 'react';
import { FiMapPin, FiPackage, FiRadio, FiStar, FiTruck, FiX } from 'react-icons/fi';
import { Button, cn, IconButton } from '../../../design-system';
import { openExternalUrl } from '../../../services/webviewService';
import { ProductDetailModal } from './ProductDetailModal';
import {
  ProductActions,
  ProductCopy,
  ProductFeedback,
  ProductMedia,
  ProductSummary,
  ProductVariants,
  useProductCart,
} from './product';

interface SearchProduct extends ProductSummary {
  link: string;
  is_same_city?: boolean;
  is_vendor_online?: boolean;
  is_best_vendor?: boolean;
}

interface ProductSearchResultsProps {
  products: SearchProduct[];
  onProductClick: (productId: number) => void;
  agent_chat_id?: number;
}

const FilterChip = ({ active, children, onClick }: { active: boolean; children: React.ReactNode; onClick: () => void }) => (
  <button
    type="button"
    aria-pressed={active}
    onClick={onClick}
    className={cn(
      'flex h-[34px] shrink-0 items-center gap-1.5 rounded-ds-6 border px-2.5 text-xs font-medium transition-colors',
      active ? 'border-ds-600 bg-ds-subtle font-bold text-ds-ink' : 'border-ds-100 bg-ds-white text-ds-700 hover:bg-ds-subtle',
    )}
  >
    {children}
  </button>
);

const SearchProductTile = ({ product, onProductClick, agentChatId }: { product: SearchProduct; onProductClick: (id: number) => void; agentChatId?: number }) => {
  const [showDetail, setShowDetail] = useState(false);
  const cart = useProductCart(product.product_id, { agentChatId });
  const mediaProduct = { ...product, is_top_vendor: product.is_best_vendor };

  const openProduct = () => {
    onProductClick(product.product_id);
    openExternalUrl(product.link);
  };

  return (
    <article className="group flex min-w-0 flex-col overflow-hidden rounded-ds-16 border border-ds-100 bg-ds-white transition-[border-color,box-shadow,transform] hover:-translate-y-0.5 hover:border-ds-200 hover:shadow-ds-2">
      <button type="button" onClick={openProduct} className="block text-right">
        <ProductMedia product={mediaProduct} />
        <div className="flex min-h-[180px] flex-col p-3 app-sm:min-h-[194px] app-sm:p-4">
          <ProductCopy product={product} price={cart.selectedPrice} />
        </div>
      </button>

      {cart.showVariants && cart.variantOptions.length > 0 ? (
        <div className="border-t border-ds-100 p-2.5">
          <ProductVariants options={cart.variantOptions} selectedId={cart.selectedVariantId} onSelect={cart.setSelectedVariantId} />
          <Button size="sm" fullWidth className="mt-2" onClick={() => cart.addToCart()} disabled={cart.addingToCart || !cart.selectedVariantId}>
            {cart.addingToCart ? 'در حال افزودن...' : 'تأیید و افزودن'}
          </Button>
          <ProductFeedback message={cart.message} />
        </div>
      ) : (
        <div className="px-3 pb-3 app-sm:px-4 app-sm:pb-4">
          <ProductActions onAdd={cart.requestAddToCart} onDetail={() => setShowDetail(true)} loading={cart.loadingVariants || cart.addingToCart} />
          <ProductFeedback message={cart.message} />
        </div>
      )}

      <ProductDetailModal productId={product.product_id} isOpen={showDetail} onClose={() => setShowDetail(false)} agent_chat_id={agentChatId} />
    </article>
  );
};

export const ProductSearchResults: React.FC<ProductSearchResultsProps> = ({ products, onProductClick, agent_chat_id }) => {
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [selectedCities, setSelectedCities] = useState<Set<string>>(new Set());
  const [showCityModal, setShowCityModal] = useState(false);
  const [filterFreeShipping, setFilterFreeShipping] = useState(false);
  const [filterSameCity, setFilterSameCity] = useState(false);
  const [filterVendorOnline, setFilterVendorOnline] = useState(false);
  const [filterBestVendor, setFilterBestVendor] = useState(false);

  const availableCities = useMemo(
    () => Array.from(new Set(products.map((product) => product.city).filter(Boolean) as string[])).sort(),
    [products],
  );

  const filteredProducts = useMemo(() => products.filter((product) => {
    const price = typeof product.price === 'string' ? Number(product.price) : product.price;
    if (price !== undefined) {
      if (priceMin && price < Number(priceMin)) return false;
      if (priceMax && price > Number(priceMax)) return false;
    }
    if (selectedCities.size > 0 && product.city && !selectedCities.has(product.city)) return false;
    if (filterFreeShipping && !product.is_free_shipping) return false;
    if (filterSameCity && !product.is_same_city) return false;
    if (filterVendorOnline && !product.is_vendor_online) return false;
    if (filterBestVendor && !product.is_best_vendor) return false;
    return true;
  }), [filterBestVendor, filterFreeShipping, filterSameCity, filterVendorOnline, priceMax, priceMin, products, selectedCities]);

  const toggleCity = (city: string) => setSelectedCities((current) => {
    const next = new Set(current);
    next.has(city) ? next.delete(city) : next.add(city);
    return next;
  });

  const clearAllFilters = () => {
    setPriceMin('');
    setPriceMax('');
    setSelectedCities(new Set());
    setFilterFreeShipping(false);
    setFilterSameCity(false);
    setFilterVendorOnline(false);
    setFilterBestVendor(false);
  };

  const hasActiveFilters = Boolean(priceMin || priceMax || selectedCities.size || filterFreeShipping || filterSameCity || filterVendorOnline || filterBestVendor);

  return (
    <section className="space-y-3">
      <div className="ds-scrollbar flex items-center gap-2 overflow-x-auto pb-2">
        <strong className="shrink-0 text-[13px] text-ds-ink">{filteredProducts.length.toLocaleString('fa-IR')} محصول</strong>
        <span className="h-6 w-px shrink-0 bg-ds-100" />
        <FilterChip active={filterFreeShipping} onClick={() => setFilterFreeShipping((value) => !value)}><FiTruck />ارسال رایگان</FilterChip>
        <FilterChip active={filterSameCity} onClick={() => setFilterSameCity((value) => !value)}><FiMapPin />همشهری</FilterChip>
        <FilterChip active={filterVendorOnline} onClick={() => setFilterVendorOnline((value) => !value)}><FiRadio />غرفه‌دار آنلاین</FilterChip>
        <FilterChip active={filterBestVendor} onClick={() => setFilterBestVendor((value) => !value)}><FiStar />غرفه برتر</FilterChip>
        {availableCities.length > 0 && (
          <FilterChip active={selectedCities.size > 0} onClick={() => setShowCityModal(true)}>
            <FiMapPin />{selectedCities.size ? `شهر (${selectedCities.size.toLocaleString('fa-IR')})` : 'شهر'}
          </FilterChip>
        )}
        <div className="flex shrink-0 items-center gap-1.5">
          <input type="number" inputMode="numeric" value={priceMin} onChange={(event) => setPriceMin(event.target.value)} placeholder="حداقل" aria-label="کمترین قیمت" className="h-[34px] w-20 rounded-ds-6 border border-ds-200 bg-ds-white px-2 text-center text-[11px] text-ds-ink outline-none focus:border-ds-primary focus:ring-[3px] focus:ring-ds-primary-ring" />
          <span className="text-xs text-ds-400">تا</span>
          <input type="number" inputMode="numeric" value={priceMax} onChange={(event) => setPriceMax(event.target.value)} placeholder="بیشترین" aria-label="بیشترین قیمت" className="h-[34px] w-20 rounded-ds-6 border border-ds-200 bg-ds-white px-2 text-center text-[11px] text-ds-ink outline-none focus:border-ds-primary focus:ring-[3px] focus:ring-ds-primary-ring" />
        </div>
        {hasActiveFilters && <Button size="sm" variant="text" onClick={clearAllFilters}><FiX />پاک‌کردن</Button>}
      </div>

      {filteredProducts.length > 0 ? (
        <div className="grid grid-cols-2 gap-2 app-sm:gap-3 app-lg:grid-cols-4">
          {filteredProducts.map((product) => (
            <SearchProductTile key={product.product_id} product={product} onProductClick={onProductClick} agentChatId={agent_chat_id} />
          ))}
        </div>
      ) : (
        <div className="grid min-h-48 place-items-center rounded-ds-12 border border-dashed border-ds-200 bg-ds-white px-4 text-center text-xs text-ds-500">
          <div><FiPackage className="mx-auto mb-3 text-ds-300" size={32} /><p>محصولی با این فیلترها پیدا نکردم.</p></div>
        </div>
      )}

      {showCityModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 app-sm:items-center app-sm:p-4" onClick={() => setShowCityModal(false)}>
          <div className="flex max-h-[80vh] w-full flex-col rounded-t-ds-16 bg-ds-white shadow-ds-3 app-sm:max-w-lg app-sm:rounded-ds-16" onClick={(event) => event.stopPropagation()}>
            <header className="flex items-center gap-3 border-b border-ds-100 p-4">
              <h3 className="text-[15px] font-bold text-ds-ink">شهرت رو انتخاب کن</h3>
              <IconButton className="mr-auto" onClick={() => setShowCityModal(false)} aria-label="بستن"><FiX /></IconButton>
            </header>
            <div className="ds-scrollbar grid flex-1 grid-cols-2 gap-2 overflow-y-auto p-4">
              {availableCities.map((city) => (
                <button key={city} type="button" onClick={() => toggleCity(city)} className={cn('min-h-10 rounded-ds-8 border px-3 text-xs', selectedCities.has(city) ? 'border-ds-primary bg-ds-primary-soft font-bold text-ds-primary-strong' : 'border-ds-100 bg-ds-subtle text-ds-700')}>{city}</button>
              ))}
            </div>
            <footer className="flex gap-2 border-t border-ds-100 p-4">
              {selectedCities.size > 0 && <Button variant="secondary" fullWidth onClick={() => setSelectedCities(new Set())}>پاک‌کردن همه</Button>}
              <Button fullWidth onClick={() => setShowCityModal(false)}>تایید ({selectedCities.size.toLocaleString('fa-IR')})</Button>
            </footer>
          </div>
        </div>
      )}
    </section>
  );
};
